import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Save, MessageCircle, Mail, Phone, MapPin, Calendar } from "lucide-react";
import { whatsAppSimple } from "../../lib/api";

const STATUSES = ["new", "being_arranged", "options_ready", "awaiting_confirmation", "confirmed", "in_progress", "completed", "cancelled"];
const CONCIERGES = ["", "Élodie", "Marco", "Sofia", "Rashid"];

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-3 text-sm text-vm-text2">
    <Icon strokeWidth={1.2} size={15} className="text-vm-accent mt-0.5 shrink-0" />
    <span className="break-all">{children}</span>
  </div>
);

export const RequestDetail = ({ request, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState(request || {});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setForm(request || {}); setConfirmDelete(false); }, [request]);

  if (!request) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const margin = (Number(form.client_price) || 0) - (Number(form.supplier_cost) || 0);

  const save = async () => {
    setSaving(true);
    await onSave(request.id, {
      status: form.status,
      assigned_concierge: form.assigned_concierge || "",
      internal_notes: form.internal_notes || "",
      confirmation_number: form.confirmation_number || "",
      supplier_cost: Number(form.supplier_cost) || 0,
      client_price: Number(form.client_price) || 0,
    });
    setSaving(false);
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.aside
          initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-xl h-full overflow-y-auto bg-vm-surface border-l border-vm-border p-7 md:p-9"
          data-testid="request-detail-panel"
        >
          <button onClick={onClose} className="absolute top-6 right-6 text-vm-muted hover:text-vm-text" data-testid="detail-close-button">
            <X strokeWidth={1.2} size={20} />
          </button>

          <p className="vm-label">{request.source?.replace(/_/g, " ")}</p>
          <h2 className="font-serif text-3xl font-light mt-2" data-testid="detail-client-name">{request.name}</h2>
          <div className="w-10 h-px bg-vm-accent my-5" />

          <div className="space-y-2.5 mb-8">
            {request.phone && <Row icon={Phone}>{request.phone}</Row>}
            {request.email && <Row icon={Mail}>{request.email}</Row>}
            {request.location && <Row icon={MapPin}>{request.location}</Row>}
            {request.date && <Row icon={Calendar}>{request.date}</Row>}
            <Row icon={Calendar}>{new Date(request.created_at).toLocaleString()}</Row>
          </div>

          <p className="vm-label mb-2">Request</p>
          <p className="text-vm-text2 leading-relaxed whitespace-pre-line border border-vm-border p-4 mb-8" data-testid="detail-message">
            {request.message || "—"}
          </p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <label className="block">
              <span className="vm-label">Status</span>
              <select value={form.status || "new"} onChange={set("status")} className="vm-input mt-2 capitalize" data-testid="detail-status-select">
                {STATUSES.map((s) => <option key={s} value={s} className="bg-vm-surface capitalize">{s.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="vm-label">Concierge</span>
              <select value={form.assigned_concierge || ""} onChange={set("assigned_concierge")} className="vm-input mt-2" data-testid="detail-concierge-select">
                {CONCIERGES.map((c) => <option key={c} value={c} className="bg-vm-surface">{c || "—"}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="vm-label">Supplier cost (€)</span>
              <input type="number" value={form.supplier_cost ?? 0} onChange={set("supplier_cost")} className="vm-input mt-2" data-testid="detail-cost-input" />
            </label>
            <label className="block">
              <span className="vm-label">Client price (€)</span>
              <input type="number" value={form.client_price ?? 0} onChange={set("client_price")} className="vm-input mt-2" data-testid="detail-price-input" />
            </label>
            <label className="block col-span-2">
              <span className="vm-label">Confirmation number</span>
              <input value={form.confirmation_number || ""} onChange={set("confirmation_number")} className="vm-input mt-2" data-testid="detail-confirmation-input" />
            </label>
          </div>

          <p className="text-sm text-vm-text2 mb-6" data-testid="detail-margin">
            Margin: <span className={margin >= 0 ? "text-vm-accent" : "text-red-400"}>€{margin.toLocaleString()}</span>
          </p>

          <label className="block mb-8">
            <span className="vm-label">Internal notes</span>
            <textarea rows={4} value={form.internal_notes || ""} onChange={set("internal_notes")} className="vm-input mt-2 resize-none" data-testid="detail-notes-input" />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={save} disabled={saving} className="vm-btn-primary px-6 py-3 text-xs tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-50" data-testid="detail-save-button">
              <Save strokeWidth={1.5} size={15} /> {saving ? "Saving…" : "Save"}
            </button>
            {request.phone && (
              <a href={whatsAppSimple(`VELMONTÉ — Hello ${request.name}, regarding your request…`)} target="_blank" rel="noreferrer"
                className="vm-btn-ghost px-6 py-3 text-xs tracking-widest uppercase inline-flex items-center gap-2" data-testid="detail-whatsapp-link">
                <MessageCircle strokeWidth={1.5} size={15} /> WhatsApp
              </a>
            )}
            {confirmDelete ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-vm-muted uppercase tracking-widest">Sure?</span>
                <button onClick={() => onDelete(request.id)} className="px-4 py-3 border border-red-400/50 text-red-400 uppercase tracking-widest hover:bg-red-400/10" data-testid="detail-delete-confirm">
                  Delete
                </button>
                <button onClick={() => setConfirmDelete(false)} className="px-4 py-3 border border-vm-border text-vm-muted uppercase tracking-widest" data-testid="detail-delete-cancel">
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="px-6 py-3 text-xs tracking-widest uppercase text-vm-muted hover:text-red-400 inline-flex items-center gap-2" data-testid="detail-delete-button">
                <Trash2 strokeWidth={1.5} size={15} /> Delete
              </button>
            )}
          </div>
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
};
